// Generated by GitHub Copilot
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import type UserCompany from '#core/entities/auth/users/UserCompany.ts'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import type { CompanyCostRate, UserCostRate } from '#core/entities/operations/settings/CostRate.ts'
import type { PriceRates } from '#core/entities/operations/shared/Price.ts'
import { usePriceRates } from '#core/state/PriceRatesState.ts'
import { useUserState } from '#core/state/UserState.ts'
import { PriceDisplay } from '#components/domain/operations/parts/shared/price-display.tsx'
import { RequirementIndicator } from '#components/entities/users/requirements/requirement-indicator.tsx'
import { useSsoProviders } from '#components/entities/users/hooks/use-sso-providers.ts'
import { SettingsTable } from './settings-table.tsx'

interface UsersTabProps {
  users: UserCompany[]
  totalCount: number
  roles: RoleDetail[]
  /** Whether to show the Rate column (the viewer can view costs). */
  showRates: boolean
  /** Users' own hourly rates; a user without one shows the company default. */
  userRates: UserCostRate[]
  /** The company default rate; null when none is set. */
  defaultRate: CompanyCostRate | null
  onEdit: (userId: string) => void
  onDelete: (userId: string) => void
  onToggleEnabled: (userId: string, enabled: boolean) => void
}

/** Users table. Only a super user may enable or disable a member; everyone else sees the state read-only. */
export function UsersTab({ users, totalCount, roles, showRates, userRates, defaultRate, onEdit, onDelete, onToggleEnabled }: UsersTabProps) {
  const { t } = useTranslation('settings')
  const { rates } = usePriceRates()
  const canToggleEnabled = useUserState().isSuperAdmin
  const showSso = useSsoProviders().hasSsoProvider

  const roleById = new Map(roles.map(r => [r.id, r]))
  const rateByUserId = new Map(userRates.map(r => [r.user_id, r]))

  return (
    <SettingsTable
      isEmpty={users.length === 0}
      emptyLabel={t('usersTeams.noUsers')}
      countLabel={t('usersTeams.showing', { count: users.length, total: totalCount })}
      headers={
        <>
          <th>{t('shared.fields.name')}</th>
          <th>{t('shared.fields.email')}</th>
          <th>{t('shared.fields.role')}</th>
          {showRates && <th>{t('usersTeams.userTable.rate')}</th>}
          <th>{t('signInRequirements.column.mfa')}</th>
          {showSso && <th>{t('signInRequirements.column.sso')}</th>}
          {canToggleEnabled && <th>{t('shared.fields.enabled')}</th>}
          <th className="text-end">{t('shared.fields.actions')}</th>
        </>
      }
    >
      {users.map(user => (
        <UserRow
          key={user.id}
          user={user}
          roleIsDefault={user.role_id ? (roleById.get(user.role_id)?.is_default ?? false) : false}
          showRate={showRates}
          showSso={showSso}
          ownRate={rateByUserId.get(user.id) ?? null}
          defaultRate={defaultRate}
          rates={rates}
          onEdit={() => onEdit(user.id)}
          onDelete={() => onDelete(user.id)}
          onToggleEnabled={canToggleEnabled ? enabled => onToggleEnabled(user.id, enabled) : null}
        />
      ))}
    </SettingsTable>
  )
}

interface UserRowProps {
  user: UserCompany
  roleIsDefault: boolean
  showRate: boolean
  showSso: boolean
  ownRate: UserCostRate | null
  defaultRate: CompanyCostRate | null
  rates: PriceRates | null
  onEdit: () => void
  onDelete: () => void
  /** Null hides the toggle and marks a disabled member with a badge instead. */
  onToggleEnabled: ((enabled: boolean) => void) | null
}

function UserRow({ user, roleIsDefault, showRate, showSso, ownRate, defaultRate, rates, onEdit, onDelete, onToggleEnabled }: UserRowProps) {
  const { t } = useTranslation('settings')

  const customPermissionCount = user.personal_permissions?.length ?? 0

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div
            className="ynt-user-avatar-circle"
            style={{ width: '2rem', height: '2rem' }}
          >
            {user.initials}
          </div>
          <span className="fw-medium">{user.displayName}</span>
          {!user.enabled && !onToggleEnabled && (
            <span className="badge bg-secondary bg-opacity-10 text-secondary">{t('usersTeams.userTable.disabled')}</span>
          )}
        </div>
      </td>
      <td className="text-secondary">{user.email}</td>
      <td>
        {user.role_id && user.role_name ? (
          <span className="d-inline-flex align-items-center gap-1">
            <span className="badge bg-primary bg-opacity-10 text-primary">{user.role_name}</span>
            {roleIsDefault && (
              <span className="badge bg-secondary bg-opacity-10 text-secondary">
                {t('usersTeams.rolesModal.defaultGroup')}
              </span>
            )}
          </span>
        ) : customPermissionCount > 0 ? (
          <span className="badge" style={{ backgroundColor: '#ffd7c4', color: '#7a3b1d' }}>
            {t('usersTeams.userTable.customPermissions')} · {customPermissionCount}
          </span>
        ) : (
          <span className="text-secondary">—</span>
        )}
      </td>
      {showRate && (
        <td>
          {ownRate
            ? <PriceDisplay price={rates ? ownRate.formatted(rates) : null} />
            : <span className="text-secondary"><PriceDisplay price={rates && defaultRate ? defaultRate.formatted(rates) : null} /></span>}
        </td>
      )}
      <td><RequirementIndicator required={user.mfa_required} /></td>
      {showSso && <td><RequirementIndicator required={user.sso_required} /></td>}
      {onToggleEnabled && (
        <td>
          <div className="form-check form-switch mb-0 d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input"
              role="switch"
              aria-label={t('shared.fields.enabled')}
              checked={user.enabled}
              onChange={e => onToggleEnabled(e.target.checked)}
            />
          </div>
        </td>
      )}
      <td className="text-end">
        <button type="button" className="btn btn-sm btn-icon p-1" title={t('shared.actions.edit')} onClick={onEdit}>
          <Pencil size={16} className="text-secondary" />
        </button>
        <button type="button" className="btn btn-sm btn-icon p-1 ms-1" title={t('shared.actions.delete')} onClick={onDelete}>
          <Trash2 size={16} className="text-danger" />
        </button>
      </td>
    </tr>
  )
}
